import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sparkles, Save, Unlock, RefreshCw } from 'lucide-react';

const fmt = (n, d = 4) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d });

export default function BgnStakingAdminPanel() {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState({});
  const [draft, setDraft] = useState({});
  const [stakes, setStakes] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [saving, setSaving] = useState(false);
  const [confirmUnlock, setConfirmUnlock] = useState(null);
  const [unlockBusy, setUnlockBusy] = useState(false);

  const loadConfig = async () => {
    try {
      const r = await api.get('/admin/staking/bgn/config');
      setCfg(r.data.config);
      setStats(r.data.stats || {});
      setDraft({
        monthly_rate_pct: (r.data.config.monthly_rate * 100).toString(),
        lock_months: r.data.config.lock_months.toString(),
        usdt_per_bgn: r.data.config.usdt_per_bgn.toString(),
        min_stake_usdt: r.data.config.min_stake_usdt.toString(),
        enabled: !!r.data.config.enabled,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load BGN config');
    }
  };

  const loadStakes = async () => {
    try {
      const q = statusFilter ? `?status=${statusFilter}` : '';
      const r = await api.get(`/admin/staking/bgn/stakes${q}`);
      setStakes(r.data.stakes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load stakes');
    }
  };

  useEffect(() => { loadConfig(); }, []);
  useEffect(() => { loadStakes(); }, [statusFilter]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        monthly_rate: parseFloat(draft.monthly_rate_pct) / 100,
        lock_months: parseInt(draft.lock_months, 10),
        usdt_per_bgn: parseFloat(draft.usdt_per_bgn),
        min_stake_usdt: parseFloat(draft.min_stake_usdt),
        enabled: !!draft.enabled,
      };
      await api.put('/admin/staking/bgn/config', body);
      toast.success('BGN staking config updated');
      await loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const forceUnlock = async () => {
    if (!confirmUnlock) return;
    setUnlockBusy(true);
    try {
      await api.post(`/admin/staking/bgn/force-unlock/${confirmUnlock.stake_id}`);
      toast.success(`Stake ${confirmUnlock.stake_id} unlocked. User can now claim.`);
      setConfirmUnlock(null);
      await loadStakes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unlock failed');
    } finally { setUnlockBusy(false); }
  };

  if (!cfg) return <div className="p-6 text-[#848E9C] text-sm">Loading BGN staking config…</div>;

  return (
    <div className="space-y-6" data-testid="admin-bgn-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FCD535]">
            <Sparkles className="w-3 h-3" /> BGN Staking Plan
          </div>
          <div className="text-white text-lg font-semibold mt-1">Manage the official BITGONODE token staking plan</div>
        </div>
        <Button
          onClick={() => { loadConfig(); loadStakes(); }}
          className="bg-[#2B3139] hover:bg-[#3A424C] text-white rounded-sm h-8 text-xs"
          data-testid="bgn-admin-refresh-btn"
        >
          <RefreshCw className="w-3 h-3 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">Total stakes</div>
          <div className="text-white font-mono text-lg mt-1">{stats.total_stakes ?? 0}</div>
        </div>
        <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">Active</div>
          <div className="text-[#FCD535] font-mono text-lg mt-1">{stats.active_stakes ?? 0}</div>
        </div>
        <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">Claimed</div>
          <div className="text-[#0ECB81] font-mono text-lg mt-1">{stats.claimed_stakes ?? 0}</div>
        </div>
        <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">Locked USDT</div>
          <div className="text-white font-mono text-lg mt-1">{fmt(stats.locked_usdt, 2)}</div>
        </div>
        <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">Locked BGN (principal)</div>
          <div className="text-white font-mono text-lg mt-1">{fmt(stats.locked_bgn_principal, 2)}</div>
        </div>
      </div>

      {/* Config editor */}
      <div className="border border-[#2B3139] bg-[#181A20] p-4">
        <div className="text-white text-sm font-semibold mb-4">Plan Terms</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Monthly interest (%)</label>
            <Input
              type="number" step="0.01" value={draft.monthly_rate_pct ?? ''}
              onChange={e => setDraft({ ...draft, monthly_rate_pct: e.target.value })}
              className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
              data-testid="bgn-admin-rate-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Lock period (months)</label>
            <Input
              type="number" min="1" step="1" value={draft.lock_months ?? ''}
              onChange={e => setDraft({ ...draft, lock_months: e.target.value })}
              className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
              data-testid="bgn-admin-lock-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">USDT per BGN</label>
            <Input
              type="number" step="0.0001" value={draft.usdt_per_bgn ?? ''}
              onChange={e => setDraft({ ...draft, usdt_per_bgn: e.target.value })}
              className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
              data-testid="bgn-admin-price-input"
            />
            <div className="text-[10px] text-[#848E9C] mt-1">1 = 1 USDT → 1 BGN</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Min stake (USDT)</label>
            <Input
              type="number" step="1" value={draft.min_stake_usdt ?? ''}
              onChange={e => setDraft({ ...draft, min_stake_usdt: e.target.value })}
              className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
              data-testid="bgn-admin-min-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Plan enabled</label>
            <div className="mt-1 flex items-center gap-2">
              <button
                onClick={() => setDraft({ ...draft, enabled: !draft.enabled })}
                className={`px-3 h-9 text-xs font-mono uppercase tracking-wider border ${draft.enabled ? 'bg-[#0ECB81]/10 border-[#0ECB81]/40 text-[#0ECB81]' : 'bg-[#F6465D]/10 border-[#F6465D]/40 text-[#F6465D]'}`}
                data-testid="bgn-admin-toggle-enabled"
              >
                {draft.enabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button
            onClick={save} disabled={saving}
            className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm h-9 px-5"
            data-testid="bgn-admin-save-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving…' : 'Save changes'}
          </Button>
          <div className="text-xs text-[#848E9C]">
            Current: <span className="text-[#FCD535] font-mono">{(cfg.monthly_rate * 100).toFixed(2)}%/mo</span> ·
            lock <span className="text-white font-mono">{cfg.lock_months} mo</span> ·
            rate <span className="text-white font-mono">1 USDT = {fmt(1 / (cfg.usdt_per_bgn || 1), 4)} BGN</span>
          </div>
        </div>
        <div className="mt-3 text-[10px] text-[#848E9C] leading-relaxed">
          Note: Config changes only affect <em>new</em> stakes. Existing stakes keep the terms snapshotted at the time of staking.
        </div>
      </div>

      {/* Stakes table */}
      <div className="border border-[#2B3139] bg-[#181A20]">
        <div className="px-4 py-3 border-b border-[#2B3139] flex items-center justify-between flex-wrap gap-2">
          <div className="text-white text-sm font-semibold">User Stakes</div>
          <div className="inline-flex border border-[#2B3139] p-0.5">
            {['active', 'unlocked', 'claimed', ''].map(s => (
              <button
                key={s || 'all'}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 text-[10px] uppercase font-mono tracking-wider ${statusFilter === s ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6] hover:text-white'}`}
                data-testid={`bgn-admin-filter-${s || 'all'}`}
              >
                {s || 'all'}
              </button>
            ))}
          </div>
        </div>
        <div className="hidden md:grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]/60">
          <div className="col-span-3">User</div>
          <div className="col-span-1">USDT</div>
          <div className="col-span-1">BGN prin.</div>
          <div className="col-span-1">Rate</div>
          <div className="col-span-1">Lock</div>
          <div className="col-span-2">Unlock at</div>
          <div className="col-span-1">Accrued</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        {stakes.length === 0 ? (
          <div className="p-6 text-center text-[#848E9C] text-sm">No stakes.</div>
        ) : stakes.map(s => (
          <div key={s.stake_id} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b border-[#2B3139]/40 items-center text-xs" data-testid={`bgn-admin-row-${s.stake_id}`}>
            <div className="md:col-span-3 text-white font-mono truncate">
              <div className="truncate">{s.email || s.user_id}</div>
              <div className="text-[10px] text-[#848E9C] truncate">{s.stake_id}</div>
            </div>
            <div className="md:col-span-1 font-mono text-white">{fmt(s.usdt_staked, 2)}</div>
            <div className="md:col-span-1 font-mono text-white">{fmt(s.bgn_principal, 2)}</div>
            <div className="md:col-span-1 font-mono text-[#FCD535]">{(s.monthly_rate * 100).toFixed(1)}%</div>
            <div className="md:col-span-1 font-mono text-white">{s.lock_months}m</div>
            <div className="md:col-span-2 text-[#B7BDC6]">{new Date(s.unlock_at).toLocaleDateString()}</div>
            <div className="md:col-span-1 font-mono text-[#0ECB81]">+{fmt(s.accrued_bgn, 2)}</div>
            <div className="md:col-span-1">
              <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${
                s.status === 'active' ? 'bg-[#FCD535]/10 text-[#FCD535]' :
                s.status === 'unlocked' ? 'bg-[#0ECB81]/10 text-[#0ECB81]' :
                s.status === 'claimed' ? 'bg-[#848E9C]/10 text-[#848E9C]' :
                'bg-[#F6465D]/10 text-[#F6465D]'
              }`}>{s.status}</span>
            </div>
            <div className="md:col-span-1 text-right">
              {s.status === 'active' && (
                <Button
                  onClick={() => setConfirmUnlock(s)}
                  size="sm"
                  className="bg-[#2B3139] hover:bg-[#3A424C] text-[#FCD535] rounded-sm h-6 text-[10px] px-2"
                  data-testid={`bgn-admin-unlock-${s.stake_id}`}
                >
                  <Unlock className="w-3 h-3 mr-1" /> Unlock
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Confirm unlock dialog */}
      <Dialog open={!!confirmUnlock} onOpenChange={(o) => !o && setConfirmUnlock(null)}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Force-unlock stake?</DialogTitle></DialogHeader>
          <div className="text-sm text-[#B7BDC6] space-y-2">
            <p>You are about to unlock <span className="font-mono text-[#FCD535]">{confirmUnlock?.stake_id}</span> before its scheduled maturity.</p>
            <p>User <span className="text-white">{confirmUnlock?.email}</span> will be able to claim <span className="font-mono text-white">{fmt(confirmUnlock?.bgn_principal, 4)} BGN principal</span> plus <span className="font-mono text-[#0ECB81]">+{fmt(confirmUnlock?.accrued_bgn, 4)} BGN accrued</span> interest.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setConfirmUnlock(null)} className="bg-transparent border border-[#2B3139] hover:bg-[#2B3139] text-white rounded-sm">Cancel</Button>
            <Button onClick={forceUnlock} disabled={unlockBusy} className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold" data-testid="bgn-admin-unlock-confirm">
              {unlockBusy ? 'Unlocking…' : 'Yes, unlock'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
